/**
 * Why a customer can send something back, which reasons need photos, and whose
 * fault each one is.
 *
 * Claims about the item itself (damaged, wrong, not as described, missing
 * parts) need proof, as on Meesho and Flipkart: the seller inspects against
 * it, and an admin settles a dispute with it. "Changed my mind" reasons don't.
 *
 * The same claims are the seller's fault, which decides who pays for the
 * return pickup. The store policy: "Return shipping costs are borne by the
 * customer unless the product is defective." A seller-fault return's pickup is
 * free for the customer; any other reason's pickup fee comes out of the refund.
 *
 * The API and both apps read this one list so they can never disagree.
 */
export type ReturnFault = 'SELLER' | 'CUSTOMER';

export const RETURN_REASONS = [
  { label: 'Damaged or defective item', needsPhoto: true, fault: 'SELLER' },
  { label: 'Wrong item delivered', needsPhoto: true, fault: 'SELLER' },
  { label: 'Item not as described', needsPhoto: true, fault: 'SELLER' },
  { label: 'Missing parts or accessories', needsPhoto: true, fault: 'SELLER' },
  { label: 'Size or fit issue', needsPhoto: false, fault: 'CUSTOMER' },
  { label: 'Quality not as expected', needsPhoto: false, fault: 'CUSTOMER' },
  { label: 'No longer needed', needsPhoto: false, fault: 'CUSTOMER' },
] as const satisfies ReadonlyArray<{ label: string; needsPhoto: boolean; fault: ReturnFault }>;

/** Most photos a customer (or a seller inspecting) can attach to a return. */
export const RETURN_MAX_PHOTOS = 5;

/** The listed reason the customer's text starts with, if any. */
function reasonOf(reason: string) {
  const r = reason.trim().toLowerCase();
  return RETURN_REASONS.find((x) => r.startsWith(x.label.toLowerCase()));
}

/**
 * Whether a reason needs photos. The apps send "<reason>: <details>", so the
 * reason is matched at the start; free text that matches none needs none.
 */
export function returnReasonNeedsPhoto(reason: string): boolean {
  return reasonOf(reason)?.needsPhoto ?? false;
}

/**
 * Whose fault a return is, from the reason the customer gave, matched the same
 * way. A reason not in the list is the customer's — staff can change it when
 * approving.
 */
export function returnFaultOf(reason: string): ReturnFault {
  return reasonOf(reason)?.fault ?? 'CUSTOMER';
}
